// public/v3/src/lib/voice-recorder.js
// MediaRecorder wrapper for the Voice Log memo capture, plus pure helpers.
// CommonJS so pickSupportedMimeType is unit-testable under Jest (this repo has
// no JSX/ESM transform); voice-log.jsx imports it via esbuild's CJS interop.
// PHI note: audio lives ONLY in memory (Blob) — never IndexedDB/localStorage.

// Container/codec preference order. Safari (iOS) records audio/mp4; everything
// else prefers webm/opus. The server accepts what we send back as mimeType.
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

// PURE: first candidate the predicate accepts, else null. null → construct
// MediaRecorder with no options and read recorder.mimeType after start.
// isTypeSupported is passed in (MediaRecorder.isTypeSupported in the browser)
// so this is testable without a DOM.
function pickSupportedMimeType(isTypeSupported) {
  if (typeof isTypeSupported !== 'function') return null;
  for (const type of MIME_CANDIDATES) {
    try { if (isTypeSupported(type)) return type; } catch (_) { /* treat as unsupported */ }
  }
  return null;
}

// Blob → { base64, mimeType } with no data: prefix (same FileReader data-URL
// pattern as kiosk-capture.js fileToImageData).
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const comma = dataUrl.indexOf(',');
      if (!dataUrl.startsWith('data:') || comma < 0) { reject(new Error('unreadable audio')); return; }
      const meta = dataUrl.slice(5, comma); // e.g. "audio/webm;codecs=opus;base64"
      const mimeType = (meta.split(';')[0] || blob.type || 'audio/webm').toLowerCase();
      const base64 = dataUrl.slice(comma + 1);
      if (!base64) { reject(new Error('empty audio')); return; }
      resolve({ base64, mimeType });
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

// Tap-to-toggle recorder: start() → speak → stop() resolves { blob, mimeType,
// durationSec }. onLevel(0..1) drives the pulse ring via an AnalyserNode RMS
// meter (rAF); onElapsed(sec) drives the mm:ss readout; recording hard-caps at
// maxSeconds (auto-stops internally — a later stop() call still resolves with
// the same result). cancel() throws the audio away.
//
// iOS requirement: the AudioContext is created + resumed inside start() (a
// user gesture). On stop/cancel we stop ALL stream tracks and close the
// AudioContext so the browser's mic indicator clears immediately.
function createVoiceRecorder({ onLevel, onElapsed, maxSeconds = 90 } = {}) {
  let stream = null;
  let recorder = null;
  let audioCtx = null;
  let rafId = 0;
  let timerId = 0;
  let chunks = [];
  let startedAt = 0;
  let active = false;
  let finishPromise = null;

  function cleanup() {
    active = false;
    if (rafId) { try { cancelAnimationFrame(rafId); } catch (_) {} rafId = 0; }
    if (timerId) { clearInterval(timerId); timerId = 0; }
    if (stream) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
      stream = null;
    }
    if (audioCtx) {
      try { audioCtx.close().catch(() => {}); } catch (_) {}
      audioCtx = null;
    }
  }

  async function start() {
    if (active || finishPromise) throw new Error('recorder already used');
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    const picked = pickSupportedMimeType(
      typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function'
        ? MediaRecorder.isTypeSupported.bind(MediaRecorder)
        : null
    );
    chunks = [];
    recorder = picked ? new MediaRecorder(stream, { mimeType: picked }) : new MediaRecorder(stream);
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    recorder.start(1000); // 1s timeslice — data survives even if stop misbehaves
    startedAt = Date.now();
    active = true;

    // Level meter — AudioContext MUST be created here, inside the tap's user
    // gesture, or iOS leaves it suspended. Cosmetic: any failure is swallowed.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        audioCtx = new Ctx();
        if (audioCtx.state === 'suspended') { try { await audioCtx.resume(); } catch (_) {} }
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        const tick = () => {
          if (!active) return;
          try {
            analyser.getFloatTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
            const rms = Math.sqrt(sum / buf.length);
            // Speech RMS is small (~0.02-0.2); scale so talking reads visibly.
            if (onLevel) onLevel(Math.max(0, Math.min(1, rms * 4)));
          } catch (_) { /* meter only */ }
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      }
    } catch (_) { /* no meter — recording still works */ }

    // Elapsed callback + hard cap. The cap finalizes internally so a distracted
    // user can't record forever; the UI's stop() then joins the same promise.
    timerId = setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt) / 1000);
      if (onElapsed) onElapsed(sec);
      if (sec >= maxSeconds) finalize();
    }, 250);
  }

  function finalize() {
    if (finishPromise) return finishPromise;
    finishPromise = new Promise((resolve, reject) => {
      if (!recorder || recorder.state === 'inactive') {
        cleanup();
        reject(new Error('not recording'));
        return;
      }
      // Server bounds: int 1-300. Round, then clamp to [1, maxSeconds].
      const durationSec = Math.max(1, Math.min(maxSeconds, Math.round((Date.now() - startedAt) / 1000)));
      recorder.onstop = () => {
        const type = (recorder.mimeType || (chunks[0] && chunks[0].type) || 'audio/webm').split(';')[0] || 'audio/webm';
        const blob = new Blob(chunks, { type });
        chunks = [];
        cleanup();
        if (!blob.size) { reject(new Error('empty recording')); return; }
        resolve({ blob, mimeType: type, durationSec });
      };
      try { recorder.stop(); } catch (e) { cleanup(); reject(e); }
    });
    return finishPromise;
  }

  function stop() { return finalize(); }

  function cancel() {
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null; // discard — nothing resolves
      try { recorder.stop(); } catch (_) {}
    }
    chunks = [];
    cleanup();
  }

  return { start, stop, cancel };
}

module.exports = { pickSupportedMimeType, blobToBase64, createVoiceRecorder, MIME_CANDIDATES };
